import { Injectable } from '@angular/core';
import { NavigationExtras, Params, Router } from '@angular/router';
import { ActivationDestinationService } from './activation-destination.service';
import {
  ActivationJourneyService,
  ActivationStepId,
  activationStepToSlug,
} from './activation-journey.service';

/**
 * Single place for activation `/setup` navigations.
 * Step changes use pushState so GA4 and Clarity record each stage.
 * Illegal deep links still replace the current history entry.
 */
@Injectable({ providedIn: 'root' })
export class ActivationNavigationService {
  private syncingSetupUrl = false;

  constructor(
    private router: Router,
    private activationJourney: ActivationJourneyService,
    private activationDestination: ActivationDestinationService
  ) {}

  get isSyncingSetupUrl(): boolean {
    return this.syncingSetupUrl;
  }

  /** Journey step URL sync. pushState so GA4 and Clarity record each stage. */
  navigateSetupStep(
    stepId: ActivationStepId,
    opts?: { replaceUrl?: boolean; queryParams?: Params }
  ): Promise<boolean> {
    const slug = activationStepToSlug(stepId);
    const queryParams = this.sanitizeQuery(opts?.queryParams);
    const tree = this.router.createUrlTree(['/setup', slug], { queryParams });
    const url = this.router.serializeUrl(tree);
    return this.navigateSetupUrl(url, {
      replaceUrl: opts?.replaceUrl === true,
    });
  }

  navigateSetupUrl(commandsOrUrl: any[] | string, extras?: NavigationExtras): Promise<boolean> {
    const replaceUrl = extras?.replaceUrl === true;
    this.syncingSetupUrl = true;
    const nav =
      typeof commandsOrUrl === 'string'
        ? this.router.navigateByUrl(commandsOrUrl, { replaceUrl })
        : this.router.navigate(commandsOrUrl, { ...extras, replaceUrl });
    return nav.finally(() => {
      this.syncingSetupUrl = false;
    });
  }

  /**
   * If the requested URL is not allowed for current state/evidence, navigate to
   * the resolved destination with replaceUrl. Returns true when a redirect ran.
   */
  reconcileSetupUrl(
    requestedUrl: string,
    opts?: { allowReentry?: boolean }
  ): boolean {
    if (this.syncingSetupUrl) {
      return false;
    }
    const dest = this.activationDestination.resolve({
      state: this.activationJourney.getState(),
      requestedPath: requestedUrl,
      allowReentry: opts?.allowReentry,
    });
    const requestedPath = this.normalizePath(requestedUrl);
    const targetPath = this.normalizePath(dest.route);
    const needsRedirect =
      (!!dest.degradedFrom && requestedPath !== targetPath) ||
      (requestedPath.startsWith('/setup') &&
        targetPath !== requestedPath &&
        (dest.route === '/dashboard' || !!dest.degradedFrom));

    if (!needsRedirect) {
      return false;
    }

    void this.navigateSetupUrl(dest.route, {
      queryParams: dest.queryParams || {},
      replaceUrl: true,
    });
    return true;
  }

  private sanitizeQuery(query?: Params): Params | undefined {
    if (!query) {
      return undefined;
    }
    const clean: Params = {};
    Object.entries(query).forEach(([key, value]) => {
      if (value != null && String(value) !== '') {
        clean[key] = value;
      }
    });
    return clean;
  }

  private normalizePath(path: string | null | undefined): string {
    if (!path) {
      return '/';
    }
    return path.split('?')[0].replace(/\/+$/, '') || '/';
  }
}
